<section class="conversation message" [class.full-screen]="isFullScreen">
  <!-- It scrolls, so it takes keyboard focus: arrow keys and Page Down read a
       long conversation without a mouse (WCAG 2.1.1). -->
  <div #messageContent class="message__content" tabindex="0" role="region" aria-label="Messages">
    @if (messages.length === 0) {
      @if (!!occupationTitle()) {
        <div class="message__wrapper reply">
          <div class="message__item">
            <strong>Interested in {{ occupationTitle() }}?</strong>
            <p>
              I can analyze how well it fits your personal profile and share anything you want to know about the career
              and the education or training needed.
            </p>
            <strong>
              What would you like to know?
            </strong>
          </div>
        </div>
      } @else {
        <div class="message__wrapper reply">
          <div class="message__item">
            <p>Hello, I'm Via!</p>
            <p>
              Think of me as your career partner for the long haul. From the
              day you're considering military service to the moment you're
              planning what comes next after the uniform comes off, I'm here to
              help you explore, plan, and decide on your career and education
              roadmap with confidence.
            </p>
            <p>What can I help you work through today?</p>
          </div>
        </div>
      }
    }

    @for (message of messages; track message.id) {
      <div
        class="message__wrapper"
        [class.reply]="message.role === 'assistant'"
        [class.send]="message.role === 'user'"
      >
        @if (message.role === 'assistant') {
          <div class="message__item markdown">
            <div
              class="flex flex-col"
              [innerHTML]="
                message.content | markdown: (isSkillsSurveyComplete$ | async)
              "
            ></div>
            @if (message.goals && !message.successSaveGoal) {
              <div class="flex flex-col gap-2 mb-2">
                @if (message.goals.onetCode &&
                message.goals.onetCode !== (goals$ | async)?.onetCode) {
                  <button type="button"
                    (click)="setAsCareerGoal(message.goals)"
                    class="button-message"
                  >
                    Set as My Career Goal
                  </button>
                }
                @if (message.goals.schoolId &&
                message.goals.schoolId !== (goals$ | async)?.schoolId) {
                  <button type="button"
                    (click)="setAsSchoolGoal(message.goals)"
                    class="button-message"
                  >
                    Set as My School Goal
                  </button>
                }
                @if (message.goals.programId &&
                message.goals.edlevelId &&
                message.goals.programId !== (goals$ | async)?.schoolProgramId) {
                  <button type="button"
                    (click)="setAsAcademicProgramGoal(message.goals)"
                    class="button-message"
                  >
                    Set as My Academic Program Goal
                  </button>
                }
              </div>
            }

            <!--            @if (message.courses?.length || message.certificates?.length) {-->
            <!--              <div class="flex flex-col gap-2 mb-2">-->
            <!--                @if (message.courses?.length || message.certificates?.length) {-->
            <!--                  <button-->
            <!--                    (click)="setAsLearningSuggestions(message.id)"-->
            <!--                    class="button-message"-->
            <!--                  >-->
            <!--                    Set as My Courses/Certificates Goal-->
            <!--                  </button>-->
            <!--                }-->
            <!--              </div>-->
            <!--            }-->
          </div>
        } @else {
          <div class="message__item">
            {{ message.content }}
          </div>
        }
        <div class="message__actions">
          @if (message.role === 'user') {
            <div class="message__time">
              @if (message.createdAt.split('T')[0] === today) {
                {{ message.createdAt | localTime: 'time' }}
              } @else {
                {{ message.createdAt | localTime: 'date' }}
              }
            </div>
          }

          <div class="actions">
            <fa-icon
              (click)="onCopyMessage(message.content)"
              matTooltip="Copy"
              [icon]="copyMessage() ? ['far', 'check'] : ['fal', 'clone']"
              [class.active]="copyMessage()"
            ></fa-icon>
            @if (message.role === 'assistant') {
              <fa-icon
                (click)="onLikeMessage(message.id, message.liked)"
                matTooltip="Good Response"
                [icon]="
                  message.liked ? ['fas', 'thumbs-up'] : ['fal', 'thumbs-up']
                "
                [class.active]="message.liked"
              ></fa-icon>
              <fa-icon
                (click)="onDislikeMessage(message.id, message.disliked)"
                matTooltip="Bad Response"
                [icon]="
                  message.disliked
                    ? ['fas', 'thumbs-down']
                    : ['fal', 'thumbs-down']
                "
                [class.active]="message.disliked"
              ></fa-icon>
            }
          </div>

          @if (message.role === 'assistant') {
            <div class="message__time">
              @if (message.createdAt.split('T')[0] === today) {
                {{ message.createdAt | localTime: 'time' }}
              } @else {
                {{ message.createdAt | localTime: 'date' }}
              }
            </div>
          }
        </div>
      </div>
    }

    @if (isStreaming && streamingResponse) {
      <div class="message__wrapper reply">
        <div class="message__item">
          {{ streamingResponse }}
        </div>
      </div>
    }

    @if (isLoading && !isStreaming) {
      <div class="message__wrapper reply">
        <mat-icon class="chat-loading" svgIcon="chat-loading"></mat-icon>
      </div>
    }

    <!--
      V-1. Every Via failure that is not a rate limit used to render NOTHING:
      the error was captured into state and read only by a console.error, so
      the learner watched a spinner that never stopped above an input they
      could no longer type in.

      role="alert" because this replaces a silent failure -- a learner using a
      screen reader had even less to go on than one watching the spinner.
    -->
    @if (error && !isLoading && !isStreaming) {
      <div class="message__wrapper reply">
        <div class="message__item markdown via-error" role="alert">
          <strong>That did not work.</strong>
          <p>
            Something went wrong while Via was answering. Your question is
            saved, so you can try it again.
          </p>
          <button type="button" class="via-error-retry" (click)="retryLastMessage()">
            Try again
          </button>
        </div>
      </div>
    }

    @if (showReassuringMessage()) {
      <div class="message__wrapper reply">
        <div class="message__item markdown">
          <div class="long-response-content">
            <div>
              <strong>Taking a bit longer than usual...</strong>
              <p>
                I'm working on a comprehensive response for you. Please bear
                with me while I gather the best information.
              </p>
            </div>
          </div>
        </div>
      </div>
    }
  </div>
  <form
    [formGroup]="questionForm"
    (submit)="onSubmit()"
    class="message__chatbox chatbox"
  >
    <div class="chatbox__content">
      <textarea
        aria-label="Ask Via a question"
        row="1"
        [ngClass]="{
          disabled:
            questionForm.controls.message.disabled || !rateLimit?.allowed
        }"
        formControlName="message"
        maxlength="1000"
        placeholder="Ask a question"
        (keyup.enter)="onSubmit()"
      ></textarea>
      <div class="actions">
        <button
          aria-label="Send message"
          [disabled]="questionForm.invalid"
          type="submit"
          mat-icon-button
          class="btn-send"
        >
          <fa-icon [icon]="['fas', 'paper-plane']"></fa-icon>
        </button>
      </div>
    </div>

    <footer>
      @if (rateLimit) {
        <div class="credit-counter" [class.warning]="!rateLimit.allowed">
          <strong class="font-semibold">{{ rateLimit.remaining }}</strong>
          credits.
          <fa-icon
            [icon]="['fas', 'info-circle']"
            [cpdTooltip]="tooltip"
          ></fa-icon>
          <ng-template #tooltip>
            <div class="custom-tooltip">
              Resets in
              <strong class="font-semibold">
                {{ rateLimit.timeUntilReset }}
              </strong>
            </div>
          </ng-template>
        </div>
      }
      <section class="disclaimer">
        Via can make mistakes, always confirm with sources.
      </section>
    </footer>
  </form>
</section>
